// Type definitions for dataLayer events
export interface DataLayerEvent {
  event: string;
  [key: string]: any;
}

// PostHog event interface (all events prefixed with ph_)
export interface PostHogEvent {
  event: string; // Must start with ph_
  userEmail?: string;
  agencyName?: string;
  [key: string]: any;
}

// Initialize dataLayer if it doesn't exist
const initializeDataLayer = (): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
};

// Send event to dataLayer
export const sendToDataLayer = (event: DataLayerEvent): void => {
  if (typeof window !== 'undefined') {
    initializeDataLayer();
    window.dataLayer.push(event);
  }
};

/**
 * Send PostHog event to dataLayer (for GTM tracking)
 * All PostHog events are prefixed with 'ph_' for easy wildcard tracking in GTM
 * 
 * @param eventName - Event name (will be prefixed with 'ph_' if not already)
 * @param properties - Event properties (userEmail, agencyName, etc.)
 */
export const trackPostHogEvent = (eventName: string, properties: Record<string, any> = {}): void => {
  if (typeof window === 'undefined') return;
  
  initializeDataLayer();
  
  // Ensure event name starts with ph_
  const prefixedEventName = eventName.startsWith('ph_') ? eventName : `ph_${eventName}`;
  
  // Clean up properties - remove null/undefined values and ensure strings are not empty
  const cleanProperties: Record<string, any> = {};
  for (const key in properties) {
    const value = properties[key];
    // Only include non-null, non-undefined values
    // For strings, include even if empty (to distinguish from missing)
    if (value !== null && value !== undefined) {
      cleanProperties[key] = value;
    }
  }
  
  window.dataLayer.push({
    event: prefixedEventName,
    ...cleanProperties
  });
  
};

/**
 * Track admin events (signup, create agency, create client, navigation)
 * Automatically includes userEmail and agencyName
 */
export const trackAdminEvent = (
  eventName: string,
  properties: { userEmail?: string; agencyName?: string; [key: string]: any } = {}
): void => {
  trackPostHogEvent(eventName, properties);
};

/**
 * Track client portal events (tab clicks)
 */
export const trackClientPortalEvent = (
  eventName: string,
  properties: Record<string, any> = {}
): void => {
  trackPostHogEvent(eventName, properties);
}; 